// URL como estado da Consulta do notification-center. Superfície
// SIMPLIFICADA: dia (?date=yyyy-MM-dd, default hoje), customer
// (?customerId), transação (?search), evento (?event) e agendado
// (?scheduled). O contrato continua aceitando os params LEGADOS
// (deep-links do Dashboard: startDate/endDate/channel/provider/
// status/originType/templateUuid) — eles viram chips removíveis na
// UI e seguem indo à query. Padrão espelhado dos *-search-params.
import { useCallback } from "react"
import { useSearchParams } from "react-router-dom"

import type { NotificationSearchFiltersType } from "@/types/notification-center"
import { DateHelper } from "@/utils"

// Chaves de filtro aceitas pelo contrato da busca.
export const NOTIFICATION_SEARCH_FILTER_KEYS = [
  "page",
  "size",
  "date",
  // Escape "todas as datas": period=all desliga o default-hoje e
  // NÃO envia startDate/endDate à API (KPIs "Em fila"/"Agendados").
  "period",
  "customerId",
  "search",
  "event",
  "scheduled",
  // Legado (deep-links do Dashboard e URLs antigas).
  "startDate",
  "endDate",
  "channel",
  "provider",
  "status",
  "originType",
  "templateUuid",
] as const

// Chaves legadas SEM controle próprio na barra de filtros — quando
// presentes na URL aparecem como chips removíveis acima da tabela.
export const NOTIFICATION_SEARCH_LEGACY_KEYS = [
  "channel",
  "provider",
  "status",
  "originType",
  "templateUuid",
] as const
export type NotificationSearchLegacyKey =
  (typeof NOTIFICATION_SEARCH_LEGACY_KEYS)[number]

// Chip sintético do período legado (startDate/endDate ≠ dia único).
export type NotificationSearchLegacyChip = {
  key: NotificationSearchLegacyKey | "period"
  value: string
}

// "yyyy-MM-dd" estrito — qualquer outro valor na URL é ignorado.
const DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const readValidDate = (value?: string) =>
  value && DATE_PARAM_PATTERN.test(value)
    ? value
    : undefined

// Dia default = hoje (mesma regra de produto do Dashboard).
export const getNotificationSearchDefaultDate = () =>
  DateHelper.formatDate(new Date())

const useNotificationCenterNotificationsSearchParams =
  () => {
    const [searchParams, setSearchParams] =
      useSearchParams()
    const readParam = (key: string) =>
      searchParams.get(key) ?? undefined
    const params = {
      page: readParam("page"),
      size: readParam("size"),
      date: readParam("date"),
      period: readParam("period"),
      customerId: readParam("customerId"),
      search: readParam("search"),
      event: readParam("event"),
      scheduled: readParam("scheduled"),
      startDate: readParam("startDate"),
      endDate: readParam("endDate"),
      channel: readParam("channel"),
      provider: readParam("provider"),
      status: readParam("status"),
      originType: readParam("originType"),
      templateUuid: readParam("templateUuid"),
    }

    // ── Resolução do dia efetivo ────────────────────────────────────
    // 0. period=all ("todas as datas") desliga o default-hoje e NÃO
    //    seleciona dia — a API busca em qualquer data; 1. ?date válido
    //    vence; 2. deep-link com startDate=endDate=dia é traduzido para
    //    o dia; 3. range legado (start≠end) mantém o período como chip
    //    (sem dia selecionado); 4. default = hoje.
    const isAllDates = params.period === "all"
    const rawDate = readValidDate(params.date)
    const legacyStart = readValidDate(params.startDate)
    const legacyEnd = readValidDate(params.endDate)
    const singleDayFromRange =
      !rawDate &&
      legacyStart &&
      legacyEnd &&
      legacyStart === legacyEnd
        ? legacyStart
        : undefined
    const hasLegacyPeriod =
      !isAllDates &&
      !rawDate &&
      !singleDayFromRange &&
      Boolean(legacyStart || legacyEnd)
    const date =
      isAllDates || hasLegacyPeriod
        ? undefined
        : (rawDate ??
          singleDayFromRange ??
          getNotificationSearchDefaultDate())
    const isToday =
      !isAllDates &&
      date === getNotificationSearchDefaultDate()

    // ── Chips dos filtros legados (deep-links do Dashboard) ────────
    const legacyChips: NotificationSearchLegacyChip[] = [
      ...NOTIFICATION_SEARCH_LEGACY_KEYS.flatMap((key) => {
        const value = params[key]
        return value ? [{ key, value }] : []
      }),
      ...(hasLegacyPeriod
        ? [
            {
              key: "period" as const,
              value: [
                params.startDate ?? "…",
                params.endDate ?? "…",
              ].join(" – "),
            },
          ]
        : []),
    ]

    // ── Filtros efetivos enviados à API (sem o whiteLabelId, que é
    // absorvido do seletor global no chamador) ──────────────────────
    const queryFilters: NotificationSearchFiltersType = {
      page: params.page,
      size: params.size,
      // period=all → sem recorte de data (todas as datas do status).
      startDate: isAllDates
        ? undefined
        : (date ?? params.startDate),
      endDate: isAllDates
        ? undefined
        : (date ?? params.endDate),
      customerId: params.customerId,
      search: params.search,
      event: params.event,
      scheduled: params.scheduled,
      channel: params.channel,
      provider: params.provider,
      status: params.status,
      originType: params.originType,
      templateUuid: params.templateUuid,
    }

    const hasParams = NOTIFICATION_SEARCH_FILTER_KEYS.some(
      (key) => searchParams.has(key)
    )

    // Aplica um patch de filtros na URL: value vazio remove a chave.
    // Mudança de filtro reseta a página (page) — exceto patch de page.
    const setParams = useCallback(
      (patch: Record<string, string | undefined>) => {
        const nextParams = new URLSearchParams(searchParams)
        Object.entries(patch).forEach(([key, value]) => {
          if (value) {
            nextParams.set(key, value)
          } else {
            nextParams.delete(key)
          }
        })
        if (!("page" in patch)) {
          nextParams.delete("page")
        }
        setSearchParams(nextParams)
      },
      [searchParams, setSearchParams]
    )

    // Seleciona um dia no calendário — substitui qualquer período
    // legado (startDate/endDate saem da URL) e sai do "todas as
    // datas" (period).
    const setDate = useCallback(
      (nextDate: string) =>
        setParams({
          date: nextDate,
          startDate: undefined,
          endDate: undefined,
          period: undefined,
        }),
      [setParams]
    )

    // Botão "Hoje" / remover o chip "Todas as datas": remove o dia da
    // URL (default já é hoje), limpa o período legado e o period=all.
    const resetToToday = useCallback(
      () =>
        setParams({
          date: undefined,
          startDate: undefined,
          endDate: undefined,
          period: undefined,
        }),
      [setParams]
    )

    // Remove um chip legado da URL (período remove as duas pontas).
    const removeLegacyChip = useCallback(
      (key: NotificationSearchLegacyChip["key"]) => {
        if (key === "period") {
          setParams({
            startDate: undefined,
            endDate: undefined,
          })
          return
        }
        setParams({ [key]: undefined })
      },
      [setParams]
    )

    const clearParams = useCallback(() => {
      const nextParams = new URLSearchParams(searchParams)
      NOTIFICATION_SEARCH_FILTER_KEYS.forEach((key) =>
        nextParams.delete(key)
      )
      setSearchParams(nextParams)
    }, [searchParams, setSearchParams])

    return {
      params,
      searchParams,
      date,
      isToday,
      // "Todas as datas" ativo (period=all) — a UI reflete o modo.
      isAllDates,
      legacyChips,
      queryFilters,
      hasParams,
      setParams,
      setDate,
      resetToToday,
      removeLegacyChip,
      clearParams,
    }
  }

export default useNotificationCenterNotificationsSearchParams
